import { cn } from "@lib/cn";
import { useEffect, useState } from "react";
import { useIntersectionObserver } from "usehooks-ts";
import { useAnalyticsQuery } from "../hooks";
import { ANALYTICS_QUERY, DataByDate, DataByLocation } from "../types";
import ReadersByLocationMap from "./readers-by-location-map";
import ReadersByTimeChart from "./readers-by-time-chart";

const QUERY_KEY = {
  readers: [
    ANALYTICS_QUERY.READERS_BY_DATE,
    ANALYTICS_QUERY.READERS_BY_LOCATION,
  ],
  sessions: [
    ANALYTICS_QUERY.SESSIONS_BY_DATE,
    ANALYTICS_QUERY.SESSIONS_BY_LOCATION,
  ],
};

export const ReadersOverview = ({
  overrides,
  view,
}: {
  overrides?: Record<string, unknown> | undefined;
  view?: string;
}) => {
  "use no memo";
  const [active, setActive] = useState("readers");
  const [width, setWidth] = useState(0);
  const [left, setLeft] = useState(0);
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.1,
  });

  const { data: response, isError } = useAnalyticsQuery({
    query: QUERY_KEY[active as keyof typeof QUERY_KEY],
    overrides: overrides ?? {},
    enabled: isIntersecting,
  });

  const byDate = response?.[QUERY_KEY[active as keyof typeof QUERY_KEY][0]];
  const byLocation = response?.[QUERY_KEY[active as keyof typeof QUERY_KEY][1]];

  useEffect(() => {
    const activeTab = document.querySelector(
      active === "readers" ? ".--readers-tab" : ".--sessions-tab",
    ) as HTMLElement;
    setWidth(activeTab?.clientWidth || 0);
    setLeft(activeTab?.offsetLeft || 0);
  }, [active]);

  const isErrorByLocation = isError;
  const isErrorByDate = isError;
  const isEmptyByLocation = byLocation === null;
  const isEmptyByDate = byDate === null;

  return (
    <div
      ref={ref}
      className={cn("-xl:tw-px-4", view === "version-details" && "-xl:tw-px-0")}
    >
      <div
        className={cn(
          "tw-shadow-2xs tw-mb-6 tw-rounded-xl tw-outline tw-outline-1 tw-outline-blue/5 tw-bg-white",
          view === "version-details" && "tw-w-full tw-mb-0",
        )}
      >
        <div className="tw-relative tw-flex tw-items-center tw-border-b tw-border-solid tw-border-blue-100 tw-px-5 tw-pt-5">
          <span
            role="tab"
            tabIndex={0}
            aria-selected={active === "readers"}
            style={{ width: 74.94 }}
            className={cn(
              "--readers-tab tw-mr-6 tw-inline-block tw-cursor-pointer tw-px-2 tw-pb-5 tw-font-sans tw-text-base tw-font-normal tw-text-blue-500 tw-transition-colors tw-duration-200 hover:tw-text-blue",
              active === "readers" && "tw-text-blue tw-font-semibold",
            )}
            onClick={() => setActive("readers")}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActive("readers"); }}
          >
            Readers
          </span>
          <span
            role="tab"
            tabIndex={0}
            aria-selected={active === "sessions"}
            style={{ width: 79.25 }}
            className={cn(
              "--sessions-tab tw-mr-1 tw-inline-block tw-cursor-pointer tw-px-2 tw-pb-5 tw-font-sans tw-text-base tw-font-normal tw-text-blue-500 tw-transition-colors tw-duration-200 hover:tw-text-blue",
              active === "sessions" && "tw-text-blue tw-font-semibold",
            )}
            onClick={() => setActive("sessions")}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setActive("sessions"); }}
          >
            Sessions
          </span>
          <span
            className="tw-absolute -tw-bottom-px tw-left-6 tw-h-[2px] tw-bg-primary tw-transition-all tw-duration-200"
            style={{
              width: `${width}px`,
              transform: `translateX(${left - 24}px)`,
            }}
          />
        </div>
        <div className="-xl:tw-grid-cols-1 tw-grid tw-grid-cols-2 tw-gap-4 tw-p-5">
          <div className="-sm:tw-h-[360px] tw-h-[470px]">
            <ReadersByTimeChart
              data={byDate as DataByDate[]}
              isError={isErrorByDate}
              isEmpty={isEmptyByDate}
              active={active as "readers" | "sessions"}
              className="tw-h-full"
            />
          </div>
          <div className="-sm:tw-h-[360px] tw-h-[470px]">
            <ReadersByLocationMap
              data={byLocation as DataByLocation[]}
              isError={isErrorByLocation}
              isEmpty={isEmptyByLocation}
              active={active as "readers" | "sessions"}
              className="tw-h-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
